import { isGeoPointValid, normalizeGeoPoint } from '@@partner:helpers/util'
import { DeliveryZone, GeoPoint } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { useFetchZonesInGeoPolygon, useLazyFetchZonesInGeoPolygon } from '@@partner:graphql/query/deliveryZone/zonesInGeoPolygon.gql'
import { useFetchShopsInGeoPolygon, useLazyFetchShopsInGeoPolygon } from '@@partner:graphql/query/shop/shopsInGeoPolygon.gql'

import L, {
  DivIcon,
  LatLngLiteral,
  Polygon as LeafletEditablePolygon,
  Map as LeafletMap,
  LeafletMouseEvent
} from 'leaflet'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer
} from 'react-leaflet'
import 'leaflet-editable'

import { ROUTE_SHOPS_EDIT_SHOP, ROUTE_DELIVERY_ZONES_EDIT } from '@@partner:config/routes'
import { SLOT_DELIVERY_PROVIDERS, EXPRESS_DELIVERY_PROVIDERS } from '@@partner:config/deliveryZone'

import 'leaflet/dist/leaflet.css'
import './DeliveryZones.scss'
import MapMarker from '@components/common/MapMarker'
import { isPointInPolygon } from 'geolib'
import DrawCircleControl from './map/DrawCircleControl'
import DistanceToShopInfo from './map/DistanceToShopInfo'
import ShopIsochrone from './map/ShopIsochrone'
import ZoneLegend, { LegendItem, LegendMode } from './map/ZoneLegend'
import AddressTestControl from './map/AddressTestControl'
import { SHOP } from '@hive/status'
import { useIsMobile } from '@@partner:helpers/hooks/useMediaQuery'
import ExpandOutlineIcon from '@rsuite/icons/ExpandOutline'
import CollaspedOutlineIcon from '@rsuite/icons/CollaspedOutline'

// Палитра цветов для магазинов (контрастные цвета)
const SHOP_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e91e63', '#00bcd4', '#ff5722', '#795548',
  '#607d8b', '#8bc34a', '#ffc107', '#673ab7', '#03a9f4',
  '#ff9800', '#4caf50', '#f44336', '#2196f3', '#9c27b0'
]

// Цвета для провайдеров доставки
const PROVIDER_COLORS: Record<string, string> = {
  'inhouse': '#2ecc71',
  'yango': '#ffcc00',
  'wolt': '#00bcd4',
  'choco': '#8b4513',
  'inhouseExpress': '#27ae60',
  'yangoExpress': '#ffd700',
  'woltExpress': '#00acc1',
  'chocoExpress': '#a0522d'
}

// Получить название провайдера
const getProviderLabel = (providerId: string): string => {
  const allProviders = [ ...SLOT_DELIVERY_PROVIDERS, ...EXPRESS_DELIVERY_PROVIDERS ]
  const provider = allProviders.find(p => p.value === providerId)
  return provider?.label ?? providerId
}

// Генерация цвета для магазина по индексу
const getShopColor = (index: number): string => {
  return SHOP_COLORS[index % SHOP_COLORS.length]
}

// Вычислить центр полигона
const getPolygonCenter = (points: GeoPoint[]): LatLngLiteral => {
  if (points.length === 0) return { lat: 0, lng: 0 }
  
  const sumLat = points.reduce((acc, p) => acc + (p.latitude ?? 0), 0)
  const sumLng = points.reduce((acc, p) => acc + (p.longitude ?? 0), 0)
  
  return {
    lat: sumLat / points.length,
    lng: sumLng / points.length
  }
}

// Создать DivIcon для инфо-маркера зоны
const createZoneInfoIcon = (zone: DeliveryZone): L.DivIcon => {
  const providers = zone.deliveryProviders?.slice(0, 2).map(getProviderLabel).join(', ') ?? ''
  
  return L.divIcon({
    html: `
      <div class="zone-info-marker">
        <div class="zone-info-marker__id">#${zone.id}</div>
        <div class="zone-info-marker__name">${zone.name ?? ''}</div>
        ${providers ? `<div class="zone-info-marker__providers">${providers}</div>` : ''}
      </div>
    `,
    className: 'zone-info-marker-container',
    iconSize: [ 80, 40 ],
    iconAnchor: [ 40, 20 ]
  })
}

export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 50.326044,
  longitude: 40.480974
}

/*
  Default map zoom
*/

const DEFAULT_MAP_ZOOM = 3

/*
  Convert polygon coords into leaflet polygon
*/

const makePolygonFromZone = (zone: GeoPoint[]) =>
  L.polygon(convertGeoPointsZoneToLeafletZone(zone), { stroke: true })

/*
  Convert geo zone to leaflet compatible positions
*/

const convertGeoPointsZoneToLeafletZone = (zone: GeoPoint[]) =>
  zone.map((value) => convertGeoPointToLeafletLatLng(value))

/*
  Convert our point format to leaflet compatible
*/

export const convertGeoPointToLeafletLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

/*
  Convert leaflet coords to our format
*/

const convertLeafletLatLngToGeoPoint = (
  latLngExpr: LatLngLiteral
): GeoPoint => normalizeGeoPoint({
  latitude: latLngExpr.lat,
  longitude: latLngExpr.lng
})

/*
  Get map center by centerPoint
*/

const makeMapCenter = (centerPoint: GeoPoint): LatLngLiteral =>
  convertGeoPointToLeafletLatLng(
    isGeoPointValid(centerPoint) ? centerPoint : DEFAULT_MAP_CENTER
  )

type Props = {
  selectedZoneId?: GQL_ID | null
  onZoneSelect: (id: GQL_ID) => void
  editedZone: GeoPoint[]
  onZoneChanged: (value: GeoPoint[]) => void
}

export default function DeliveryZonesOnMap ({
  selectedZoneId,
  editedZone,
  onZoneChanged,
  onZoneSelect
}: Props) {
  const { brandSlug } = useParams<any>()
  const isMobile = useIsMobile()
  
  // Полноэкранный режим (скрывает панели)
  const [ isFullscreen, setIsFullscreen ] = useState(false)
  
  // Свёрнутый bottom sheet на мобилке
  const [ isBottomSheetCollapsed, setIsBottomSheetCollapsed ] = useState(false)
  
  // map center state
  const [ mapCenter, setMapCenter ] = useState(() =>
    makeMapCenter(DEFAULT_MAP_CENTER)
  )
  // store map ref
  const [ map, setMap ] = useState<LeafletMap>()
  const [ currentPolygonObj, setCurrentPolygonObj ] =
    useState<LeafletEditablePolygon>(() => makePolygonFromZone([]))
  const [ isDrawing, setIsDrawing ] = useState(false)
  const [ selectedZone, setSelectedZoneId ] = useState<GQL_ID>()

  // Режим легенды: магазины или провайдеры
  const [ legendMode, setLegendMode ] = useState<LegendMode>('shops')
  
  // Состояния для фильтрации магазинов
  const [ selectedShopIds, setSelectedShopIds ] = useState<Set<GQL_ID>>(new Set())
  const [ selectedProviderIds, setSelectedProviderIds ] = useState<Set<string>>(new Set())
  const [ showAllShops, setShowAllShops ] = useState(false)
  const [ shopColorsMap, setShopColorsMap ] = useState<Map<GQL_ID, string>>(new Map())

  // Состояние для тултипа при наведении/тапе на зону
  const [ hoveredZone, setHoveredZone ] = useState<DeliveryZone | null>(null)
  // Фиксированный тултип (для мобилки - по тапу)
  const [ pinnedZoneTooltip, setPinnedZoneTooltip ] = useState<DeliveryZone | null>(null)

  // Состояние для маркера тестового адреса
  const [ testAddressPoint, setTestAddressPoint ] = useState<LatLngLiteral | null>(null)
  const [ testAddress, setTestAddress ] = useState<string>('')

  const { data: zonesData, loading } = useFetchZonesInGeoPolygon({
    corners: [
      { latitude: 90, longitude: -180 },
      { latitude: 90, longitude: 180 },
      { latitude: -90, longitude: 180 },
      { latitude: -90, longitude: -180 }
    ]
  })

  const loadedZones = useMemo(() => 
    zonesData?.zonesInGeoPolygon || [], 
  [ zonesData?.zonesInGeoPolygon ]
  )

  const { data: shopsData, loading: loadingShops } =
    useFetchShopsInGeoPolygon({
      corners: [
        { latitude: 90, longitude: -180 },
        { latitude: 90, longitude: 180 },
        { latitude: -90, longitude: 180 },
        { latitude: -90, longitude: -180 }
      ]
    })

  /*   useEffect(() => {
    if (zonesData?.zonesInGeoPolygon) {
      zonesData.zonesInGeoPolygon.map((zone) => {
        if (!loadedZones.some((loadedZone) => loadedZone.id === zone.id)) {
          setLoadedZones((zones) => [ ...zones, zone ])
        }
      })
      loadedZones.map((zone) => {
        if (!zonesData.zonesInGeoPolygon.some((loadedZone) => loadedZone.id === zone.id)) {
          setLoadedZones((zones) => zones.filter((loadedZone) => loadedZone.id !== zone.id))
        }
      }
      )
    }
    
  }, [ zonesData?.zonesInGeoPolygon ]) */


  const selectedZoneObj = useMemo(() => loadedZones.find(
    (zone) => zone.id === selectedZone
  ), [ loadedZones, selectedZone ])

  // Инициализация цветов и выбранных магазинов при загрузке
  useEffect(() => {
    if (shopsData?.shopsInGeoPolygon && shopsData.shopsInGeoPolygon.length > 0) {
      const newColorsMap = new Map<GQL_ID, string>()
      const activeShopIds = new Set<GQL_ID>()

      shopsData.shopsInGeoPolygon.forEach((shop, index) => {
        if (shop.id) {
          newColorsMap.set(shop.id, getShopColor(index))
          if (shop.status === SHOP.ENABLED) {
            activeShopIds.add(shop.id)
          }
        }
      })

      setShopColorsMap(newColorsMap)
      setSelectedShopIds(activeShopIds)
    }
  }, [ shopsData?.shopsInGeoPolygon ])

  // Инициализация выбранных провайдеров при загрузке зон
  useEffect(() => {
    if (loadedZones.length > 0) {
      const allProviders = new Set<string>()
      loadedZones.forEach(zone => {
        zone.deliveryProviders?.forEach(p => allProviders.add(p))
      })
      setSelectedProviderIds(allProviders)
    }
  }, [ loadedZones ])

  // Элементы для легенды магазинов
  const shopLegendItems: LegendItem[] = useMemo(() => {
    if (!shopsData?.shopsInGeoPolygon) return []

    return shopsData.shopsInGeoPolygon
      .filter(shop => shop.id)
      .map(shop => ({
        id: shop.id!,
        name: shop.name ?? `Магазин ${shop.id}`,
        address: shop.address ?? undefined,
        color: shopColorsMap.get(shop.id!) ?? '#999',
        isActive: shop.status === SHOP.ENABLED
      }))
  }, [ shopsData?.shopsInGeoPolygon, shopColorsMap ])

  // Элементы для легенды провайдеров
  const providerLegendItems: LegendItem[] = useMemo(() => {
    const allProviders = new Set<string>()
    loadedZones.forEach(zone => {
      zone.deliveryProviders?.forEach(p => allProviders.add(p))
    })

    return Array.from(allProviders).map(providerId => ({
      id: providerId,
      name: getProviderLabel(providerId),
      color: PROVIDER_COLORS[providerId] ?? '#999'
    }))
  }, [ loadedZones ])

  // Обработчики для легенды
  const handleItemToggle = useCallback((id: GQL_ID | string) => {
    if (legendMode === 'shops') {
      setSelectedShopIds(prev => {
        const next = new Set(prev)
        if (next.has(id as GQL_ID)) {
          next.delete(id as GQL_ID)
        }
        else {
          next.add(id as GQL_ID)
        }
        return next
      })
    }
    else {
      setSelectedProviderIds(prev => {
        const next = new Set(prev)
        if (next.has(id as string)) {
          next.delete(id as string)
        }
        else {
          next.add(id as string)
        }
        return next
      })
    }
  }, [ legendMode ])

  const handleSelectAll = useCallback(() => {
    if (legendMode === 'shops') {
      const visibleShops = showAllShops
        ? shopLegendItems
        : shopLegendItems.filter(shop => shop.isActive !== false)
      setSelectedShopIds(new Set(visibleShops.map(shop => shop.id as GQL_ID)))
    }
    else {
      setSelectedProviderIds(new Set(providerLegendItems.map(p => p.id as string)))
    }
  }, [ legendMode, showAllShops, shopLegendItems, providerLegendItems ])

  const handleDeselectAll = useCallback(() => {
    if (legendMode === 'shops') {
      setSelectedShopIds(new Set())
    }
    else {
      setSelectedProviderIds(new Set())
    }
  }, [ legendMode ])

  // Фильтрованные зоны
  const filteredZones = useMemo(() => {
    if (legendMode === 'shops') {
      if (selectedShopIds.size === 0) return []
      return loadedZones.filter(zone => 
        zone.shop_id && selectedShopIds.has(zone.shop_id)
      )
    }
    else {
      if (selectedProviderIds.size === 0) return []
      return loadedZones.filter(zone => 
        zone.deliveryProviders?.some(p => selectedProviderIds.has(p))
      )
    }
  }, [ loadedZones, selectedShopIds, selectedProviderIds, legendMode ])

  // Получить цвет зоны в зависимости от режима
  const getZoneColor = useCallback((zone: DeliveryZone): string => {
    if (legendMode === 'shops') {
      return zone.shop_id ? (shopColorsMap.get(zone.shop_id) ?? '#e9833a') : '#e9833a'
    }
    else {
      const firstProvider = zone.deliveryProviders?.[0]
      return firstProvider ? (PROVIDER_COLORS[firstProvider] ?? '#e9833a') : '#e9833a'
    }
  }, [ legendMode, shopColorsMap ])

  // Центрирование карты по всем зонам после загрузки
  useEffect(() => {
    if (map && loadedZones.length > 0 && !loading) {
      const allPoints: LatLngLiteral[] = loadedZones.flatMap((zone) =>
        zone.zoneGeoDeliveryZone.map(convertGeoPointToLeafletLatLng)
      )
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints)
        map.fitBounds(bounds, { padding: [ 20, 20 ] })
      }
    }
  }, [ map, loadedZones, loading ])

  let polygonsOnMap: L.Polygon<any>[] = useMemo(() => [], [])

  const loadForBounds = useCallback(
    (map: L.Map) => {
      const bounds = map.getBounds()
      const zone = [
        convertLeafletLatLngToGeoPoint(bounds.getNorthWest()),
        convertLeafletLatLngToGeoPoint(bounds.getNorthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthWest())
      ]

      /* fetchZones({
        variables: {
          corners: zone
        }
      }) */
      /* fetchShops({
        variables: {
          corners: zone
        }
      }) */

    },
    [ ]
  )

  useLayoutEffect(() => {
    if (map) {
      map.on('dragend', () => loadForBounds(map))
    }
  }, [ map, loadForBounds ])

  useEffect(() => {
    if (!selectedZoneId) {
      setSelectedZoneId(undefined)
    }
  }, [
    selectedZoneId
  ])
  
  // Activate drawing map
  useEffect(() => {
    if (
      (selectedZoneId || selectedZoneId === null) &&
      map &&
      map.editTools &&
      editedZone.length === 0
    ) {
      const newPolygon: L.Polygon = map.editTools.startPolygon()
      map.on('editable:editing', handlePolygonEditing)
      currentPolygonObj.removeFrom(map)
      setIsDrawing(true)
      setCurrentPolygonObj(newPolygon)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedZoneId, editedZone ])

  const handlePolygonEditing = useCallback(
    (e: { layer: LeafletEditablePolygon }) => {
      const coords = e.layer.getLatLngs().flat(2)
      onZoneChanged?.(
        coords.map((latLngObj) => convertLeafletLatLngToGeoPoint(latLngObj))
      )
    },
    [ onZoneChanged ]
  )

  const handleSelectPolygon = useCallback(
    (zone: DeliveryZone) => {
      onZoneSelect(zone.id)
      onZoneChanged(zone.zoneGeoDeliveryZone)
      polygonsOnMap.map(pol => {
        pol.setStyle({ color: 'grey' })
        pol.off('click')
      })
    },
    [ onZoneChanged, onZoneSelect, polygonsOnMap ]
  )

  const [ popupInfo, setPopupInfo ] = useState<{
    position: [number, number],
    zones: DeliveryZone[]
  } | null>(null)

  const handlePolygonClick = useCallback(
    (event: L.LeafletMouseEvent, zone) => {
      const clickedZones = loadedZones.filter(
        zone =>
          isPointInPolygon(
            event.latlng,
            zone.zoneGeoDeliveryZone.map(convertGeoPointToLeafletLatLng)
          )
      )
      
      // На мобилке: первый тап показывает тултип, второй - выполняет действие
      if (isMobile) {
        const topZone = clickedZones[0]
        // Если уже показываем тултип для этой зоны - выполняем действие
        if (pinnedZoneTooltip?.id === topZone?.id) {
          setPinnedZoneTooltip(null)
          if (clickedZones.length > 1) {
            setPopupInfo({
              position: [ event.latlng.lat, event.latlng.lng ],
              zones: clickedZones
            })
          }
          else if (clickedZones.length === 1) {
            handleSelectPolygon(clickedZones[0])
          }
        }
        else {
          // Показываем тултип
          setPinnedZoneTooltip(topZone || null)
          setHoveredZone(topZone || null)
        }
        return
      }
      
      // Если в одной точке больше одной зоны, показываем popup с выбором
      if (clickedZones && clickedZones.length > 1) {
        setPopupInfo({
          position: [ event.latlng.lat, event.latlng.lng ],
          zones: clickedZones
        })
      }
      else if (clickedZones && clickedZones.length === 1) {
        handleSelectPolygon(clickedZones[0])
      }
    },
    [ loadedZones, handleSelectPolygon, isMobile, pinnedZoneTooltip ]
  )

  // Refs для колбэков, чтобы не перерисовывать полигоны при их изменении
  const handlePolygonClickRef = useRef(handlePolygonClick)
  handlePolygonClickRef.current = handlePolygonClick

  const getZoneColorRef = useRef(getZoneColor)
  getZoneColorRef.current = getZoneColor

  // Перерисовка полигонов при изменениях
  useEffect(() => {
    if (map) {
      polygonsOnMap.forEach((polygon) => polygon.removeFrom(map))
      polygonsOnMap.length = 0
      
      if (selectedZoneId === undefined) {
        currentPolygonObj.removeFrom(map)
      }
    }
    if (filteredZones && filteredZones.length > 0 && map) {
      const isEditing = selectedZoneId !== undefined
      // В режиме редактирования показываем все зоны серым, иначе — только отфильтрованные
      const zonesToRender = isEditing ? loadedZones : filteredZones

      zonesToRender.forEach((zone) => {
        if (zone.id !== selectedZoneId) {
          const zoneColor = isEditing ? '#999' : getZoneColorRef.current(zone)

          let polygon = L.polygon(
            convertGeoPointsZoneToLeafletZone(zone.zoneGeoDeliveryZone),
            {
              color: zoneColor,
              fillColor: zoneColor,
              fillOpacity: isEditing ? 0.25 : 0.3,
              weight: isEditing ? 1 : 2,
              smoothFactor: 1
            }
          ).addTo(map)

          if (!isEditing) {
            polygon.on('click', (event) => handlePolygonClickRef.current(event, zone))
            // На десктопе - hover, на мобилке используем click для показа тултипа
            if (!isMobile) {
              polygon.on('mouseover', () => setHoveredZone(zone))
              polygon.on('mouseout', () => setHoveredZone(null))
            }
          }
          polygonsOnMap.push(polygon)
        }
        else if (selectedZone !== selectedZoneId) {
          currentPolygonObj.removeFrom(map)
          map.editTools.stopDrawing()
          setSelectedZoneId(zone.id)
          const activePolygon = makePolygonFromZone(zone.zoneGeoDeliveryZone)
          setCurrentPolygonObj(activePolygon)
          activePolygon.addTo(map)
          activePolygon.enableEdit()
          map.on('editable:editing', handlePolygonEditing)
          onZoneChanged(zone.zoneGeoDeliveryZone)
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredZones,
    loadedZones,
    onZoneSelect,
    selectedZoneId,
    polygonsOnMap
  ])

  // Координаты магазинов (фильтрованные и с цветами)
  const positions = useMemo(() => {
    if (
      shopsData?.shopsInGeoPolygon
      && shopsData.shopsInGeoPolygon.length > 0
    ) {
      const shopPoints = shopsData?.shopsInGeoPolygon
        .filter(shop => {
          // Фильтруем по выбранным магазинам
          if (!shop.id) return false
          if (!selectedShopIds.has(shop.id)) return false
          // Фильтруем по активности (если не показывать все)
          if (!showAllShops && shop.status !== SHOP.ENABLED) return false
          return true
        })
        .map((shop) => {
          const selected = selectedZoneObj?.shop_id === shop.id
          if (shop.id && shop?.shopGeoPoint) {
            return {
              id: shop.id,
              name: shop.name,
              address: shop.address,
              point: convertGeoPointToLeafletLatLng(shop.shopGeoPoint),
              selected,
              color: shopColorsMap.get(shop.id) ?? 'blue'
            }
          }
          return undefined
        })
        .filter((zone) => zone !== undefined)
      return shopPoints
    }
    return []
  }, [ shopsData?.shopsInGeoPolygon, selectedZoneObj, selectedShopIds, showAllShops, shopColorsMap ])

  const whenMapCreated = (map: LeafletMap) => {
    setMap(map)
    loadForBounds(map)
  }

  const handleSetCenter = useCallback(
    (position: GeolocationPosition) => {
      const geoPoint = convertGeoPointToLeafletLatLng(position.coords)

      if (map) {
        map.setView(geoPoint, 12)
        loadForBounds(map)
      }
      setMapCenter(geoPoint)
    },
    [ map, loadForBounds ]
  )

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSetCenter)
    }
  }, [ handleSetCenter ])

  // --- Обработчик выбора адреса в AddressTestControl ---
  const handleTestAddressSelected = useCallback((point: GeoPoint, address: string) => {
    const latLng: LatLngLiteral = {
      lat: point.latitude ?? 0,
      lng: point.longitude ?? 0
    }
    setTestAddressPoint(latLng)
    setTestAddress(address)
    
    // Центрируем карту на выбранном адресе без изменения зума
    if (map) {
      map.panTo(latLng)
    }
  }, [ map ])

  // --- Добавлено для измерения расстояния ---
  // Сохраняем последнюю точку клика и расстояние
  const [ distanceMarker, setDistanceMarker ] = useState<LatLngLiteral | null>(null)
  const [ distanceToShop, setDistanceToShop ] = useState<number | null>(null)

  // Получаем координаты текущего магазина (по выбранной зоне)
  const currentShopPoint = useMemo(() => {
    if (!selectedZoneObj?.shop_id || !shopsData?.shopsInGeoPolygon) return null
    const shop = shopsData.shopsInGeoPolygon.find(
      (s) => s.id === selectedZoneObj.shop_id
    )
    return shop?.shopGeoPoint ?? null
  }, [ selectedZoneObj?.shop_id, shopsData?.shopsInGeoPolygon ])

  const [ circleRadius, setCircleRadius ] = useState<number>(0)
  const [ circle, setCircle ] = useState<L.Circle | null>(null)

  // Функция отрисовки круга с центром в координатах текущего магазина
  const handleDrawCircle = useCallback(() => {
    if (!map || !currentShopPoint) return

    // Если круг уже был нарисован ранее — удаляем его
    if (circle) {
      circle.removeFrom(map)
    }

    // Координаты магазина
    const shopLatLng = L.latLng(convertGeoPointToLeafletLatLng(currentShopPoint))

    // Создаём новый круг
    const newCircle = L.circle(shopLatLng, {
      radius: circleRadius,    // радиус, который ввели в инпут
      color: 'green',
      fillColor: '#ace3ac',
      fillOpacity: 0.4
    })

    // Добавляем на карту
    newCircle.addTo(map)

    // Сохраняем в стейт, чтобы можно было при необходимости удалить или обновить
    setCircle(newCircle)
  }, [ map, circleRadius, currentShopPoint, circle ])

  // Получаем информацию о магазине для hoveredZone
  const hoveredZoneShop = useMemo(() => {
    if (!hoveredZone || !shopsData?.shopsInGeoPolygon) return null
    return shopsData.shopsInGeoPolygon.find(s => s.id === hoveredZone.shop_id)
  }, [ hoveredZone, shopsData?.shopsInGeoPolygon ])

  return (
    <div 
      className={`DeliveryZonesOnMap ${isFullscreen ? 'DeliveryZonesOnMap--fullscreen' : ''}`}
      style={{ position: 'relative', height: '100vh', width: '100%' }}
    >
      {/* Кнопка полноэкранного режима на мобилке */}
      {isMobile && (
        <button 
          className="DeliveryZonesOnMap__FullscreenButton"
          onClick={() => {
            setIsFullscreen(!isFullscreen)
            // Сбрасываем тултип при переключении режима
            setPinnedZoneTooltip(null)
            setHoveredZone(null)
          }}
          title={isFullscreen ? 'Показать панели' : 'Полный экран'}
        >
          {isFullscreen ? <CollaspedOutlineIcon /> : <ExpandOutlineIcon />}
        </button>
      )}

      {/* Тултип при наведении на зону */}
      {hoveredZone && (
        <div 
          className={`ZoneHoverTooltip ${isMobile ? 'ZoneHoverTooltip--mobile' : ''}`}
          onClick={() => {
            // На мобилке клик на тултип закрывает его
            if (isMobile) {
              setPinnedZoneTooltip(null)
              setHoveredZone(null)
            }
          }}
        >
          <div className="ZoneHoverTooltip__row">
            <span className="ZoneHoverTooltip__label">ID:</span>
            <span className="ZoneHoverTooltip__value">{hoveredZone.id}</span>
          </div>
          <div className="ZoneHoverTooltip__row">
            <span className="ZoneHoverTooltip__label">Название:</span>
            <span className="ZoneHoverTooltip__value">{hoveredZone.name || '—'}</span>
          </div>
          <div className="ZoneHoverTooltip__row">
            <span className="ZoneHoverTooltip__label">Магазин:</span>
            <span className="ZoneHoverTooltip__value">{hoveredZoneShop?.name || '—'}</span>
          </div>
          <div className="ZoneHoverTooltip__row">
            <span className="ZoneHoverTooltip__label">Провайдеры:</span>
            <span className="ZoneHoverTooltip__value">
              {hoveredZone.deliveryProviders?.length
                ? hoveredZone.deliveryProviders.map(getProviderLabel).join(', ')
                : '—'}
            </span>
          </div>
        </div>
      )}

      {/* Правая панель: Легенда + Тест адреса */}
      <div className={`DeliveryZonesOnMap__RightPanel ${isBottomSheetCollapsed ? 'DeliveryZonesOnMap__RightPanel--collapsed' : ''}`}>
        {/* Индикатор свайпа (кликабельный на мобилке) */}
        {isMobile && (
          <div 
            className="DeliveryZonesOnMap__BottomSheetHandle"
            onClick={() => setIsBottomSheetCollapsed(!isBottomSheetCollapsed)}
          />
        )}
        
        {/* Контент панели */}
        {!isBottomSheetCollapsed && (
          <>
            {/* Легенда зон */}
            <ZoneLegend
              mode={legendMode}
              onModeChange={setLegendMode}
              items={legendMode === 'shops' ? shopLegendItems : providerLegendItems}
              selectedIds={legendMode === 'shops' ? selectedShopIds : selectedProviderIds}
              onItemToggle={handleItemToggle}
              showAllShops={showAllShops}
              onShowAllShopsChange={setShowAllShops}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />

            {/* Тест адреса */}
            <AddressTestControl
              zones={loadedZones}
              brandSlug={brandSlug}
              onAddressSelected={handleTestAddressSelected}
            />
          </>
        )}
      </div>

      <MapContainer
        center={mapCenter}
        zoom={DEFAULT_MAP_ZOOM}
        scrollWheelZoom={true}
        editable={true}
        whenCreated={whenMapCreated}
        style={{
          zIndex: 30,
          height: '100%',
          width: '100%'
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Маркеры магазинов */}
        {positions &&
          positions.length > 0 &&
          positions.map((position) => (
            <Marker
              icon={MapMarker(position.selected ? 'red' : position.color)}
              key={`marker:${position.id}`}
              position={position.point}
            >
              <Popup>
                <div>{position.id}</div>
                <div>{ position.name } {position.address}</div>
                <Link
                  to={generatePath(ROUTE_SHOPS_EDIT_SHOP, {
                    brandSlug,
                    shopId: position.id
                  })}
                  target="_blank"
                >
                  Открыть
                </Link>
              </Popup>
            </Marker>
          ))}

        {/* Popup со списком зон, если в одной точке пересеклись несколько */}
        {popupInfo && (
          <Popup position={popupInfo.position} onClose={() => setPopupInfo(null)}>
            <div>
              <h4>Зоны в точке:</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {popupInfo.zones.map(zone => (
                  <li key={zone.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ flex: 1 }}>
                      {zone.id}: {zone.name}
                    </span>
                    <a 
                      onClick={() => onZoneSelect(zone.id)}
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                    >
                      Выбрать
                    </a>
                    <Link
                      to={generatePath(ROUTE_DELIVERY_ZONES_EDIT, {
                        brandSlug,
                        zoneId: zone.id
                      })}
                      target="_blank"
                      style={{ color: '#52c41a' }}
                    >
                      Ред.
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Popup>
        )}

        {/* Маркер тестового адреса */}
        {testAddressPoint && (
          <Marker 
            icon={MapMarker('purple')} 
            position={testAddressPoint}
          >
            <Popup>
              <div style={{ maxWidth: 250 }}>
                <strong>Тестовый адрес</strong>
                <div style={{ marginTop: 4, fontSize: 12 }}>{testAddress}</div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* --- Отображаем расстояние до последней точки клика, если есть --- */}
        {distanceMarker && distanceToShop !== null && (
          <Marker icon={MapMarker('orange')} position={distanceMarker}>
            <Popup>
              <div>Расстояние от магазина: {Math.round(distanceToShop)} м</div>
            </Popup>
          </Marker>
        )}
        <DrawCircleControl
          onDrawCircle={handleDrawCircle}
          circleRadius={circleRadius}
          setCircleRadius={setCircleRadius}
        />
        <DistanceToShopInfo currentShopPoint={positions.find(shop => shop.selected)?.point} />
      </MapContainer>
    </div>
  )
}
